import { Component, ViewChild, OnInit, OnDestroy, inject, ElementRef, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeModule } from '@angular/material/tree';
import { IonicModule } from '@ionic/angular';
import { Subscription, filter, take } from 'rxjs';
import { DateTime as LuxonDateTime } from 'luxon';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';

import { ManageConnectionsPage } from 'src/pages/finance/manage-connections/manage-connections';
import { AddTransactionPage } from 'src/pages/finance/add-transaction/add-transaction';
import { TransactionDetailPage } from 'src/pages/finance/transaction-detail/transaction-detail.page';
import { TransactionRulesPage } from 'src/pages/finance/transaction-rules/transaction-rules.page';
import { ConnectionHistoryPage } from 'src/pages/finance/connection-history/connection-history.page';

import { Address } from 'src/shared/services/addresses-api.service';
import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { BankConnectFlowService } from 'src/shared/services/bank-connect-flow.service';
import { BankRowsService } from 'src/shared/services/bank-rows.service';
import { FinanceTypesService } from 'src/shared/services/finance-types.service';
import {
  PropertyTransactionsService,
  FinancialTransaction,
  FinancialTransactionFilters,
  FinancialTransactionParams,
  permissionsOf,
} from 'src/shared/services/property-transactions.service';
import { FinancialConnectionsService, FinancialConnectionsAccount } from 'src/shared/services/financial-connections.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { JOB_ICON_FALLBACK, RelatedRecords, RESERVATION_COLOR_ICON, TransactionRelatedRecordsService } from 'src/shared/services/transaction-related-records.service';
import { MONTH_ATTRIBUTION_QUERY_PARAM, MonthAttributionService, parseMonthAttribution } from 'src/shared/services/month-attribution.service';

import { paidOnLabel, periodLabel } from 'src/shared/util/transaction-dates';

import { MatTooltipModule } from '@angular/material/tooltip';

import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';

import { ChipFilter, FlatNode, ItemNode } from 'src/models/schedule.model';
import { MonthAttribution } from 'src/models/monthly-finance.model';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import { MonthAttributionSwitchComponent } from 'src/shared/components/month-attribution-switch/month-attribution-switch.component';

import csvDownload from 'json-to-csv-export';

/** One button in the Job or Reservation column: the record behind a row, named by its date. */
interface RecordChip {
  label: string;
  /** Shown inside the button when there is no guest to show initials for. */
  icon: string;
  /** The guest whose initials sit inside the reservation button. */
  initials: string;
}

interface TransactionView {
  txn: FinancialTransaction;
  /** What the opened month counts: the month's share when a P&L cell sent us here, the whole amount otherwise. */
  formattedAmount: string;
  /** "of $13,164.90" when the month counts only part of the amount. */
  wholeAmountNote: string;
  /** The cents behind `formattedAmount`, unsigned; the CSV writes the same figure. */
  amountShown: number;
  categoryLabel: string;
  propertyLabel: string;
  /** The address's own colour, or undefined when it never picked one. */
  propertyColor: string | undefined;
  sourceLabel: string;
  needsAttention: boolean;
  suggestionLabel: string;
  /** Written by TIDY from a job or a stay: shown beside the customer's rows, never edited inline. */
  isTidy: boolean;
  /**
   * The day that puts the row in the list: its period ("Aug 28 – Sep 6, 2026" on a stay), or
   * under cash in an opened month, the day it was paid.
   */
  dateLabel: string;
  /** The yyyy-MM-dd behind `dateLabel`: the paid day under cash in an opened month, the period's first day otherwise. */
  dayShown: string;
  isUpcoming: boolean;
  jobId: number | null;
  reservationId: number | null;
  job: RecordChip | null;
  reservation: RecordChip | null;
  /** From the row's `permissions`: the inline selects grey out where this member may not change the row. */
  canEditCategory: boolean;
  canEditProperty: boolean;
}

/** Filters are additive: a group holds every value picked for it, and groups narrow together. */
type FilterGroup = 'address' | 'category' | 'source';
const FILTER_GROUP_NAMES: Record<FilterGroup, string> = { address: 'Property', category: 'Category', source: 'Source' };
/** Past this many picked categories the chips collapse into one. */
const MAX_CATEGORY_CHIPS = 3;
/** The value of the one chip that stands for every picked category. */
const ALL_PICKED_CATEGORIES = '__all_picked__';

const PAGE_SIZE = 50;
const EMPTY_NO_TRANSACTIONS = 'No transactions yet. Connect a bank account and TIDY will sort its transactions into your P&L, or add one by hand.';
const EMPTY_NO_MATCHES = 'No transactions match these filters.';
const PROPERTY_PLACEHOLDER: SelectModel = { value: '', viewValue: 'Select property' };
const CATEGORY_PLACEHOLDER: SelectModel = { value: '', viewValue: 'Select category' };
const DEFAULT_ADDRESS_COLORS = ['#000000', '#1E2125'];
const ATTENTION_ICON = 'assets/img/alert-circle-outline-red.svg';
const PROPERTY_ICON = 'home';
const UPCOMING_ICON = 'assets/svg/time-outline.svg';
const UPCOMING_TOOLTIP = 'Upcoming: this has not been charged or paid yet. TIDY adds it as soon as the booking or job is known, so next month is already in your P&L.';
const SHARE_ICON = 'assets/svg/information-circle-outline.svg';
const FILTER_GROUPS: Record<string, FilterGroup> = { Property: 'address', Category: 'category', Source: 'source' };
const FILTER_ICONS: Record<FilterGroup, string> = { address: 'home', category: 'label', source: 'account_balance' };
const CSV_HEADERS = ['Date', 'Check-out', 'Description', 'Amount', 'Category', 'Property', 'Source'];

/** A tidy-table `buttons` cell holding one small button for the record, or nothing. */
function recordButtonCell(chip: RecordChip | null, action: () => void): any {
  if (!chip) { return { value: '', action: null }; }
  return {
    value: '',
    action: null,
    buttons: [{
      text: chip.label,
      image: chip.initials ? '' : chip.icon,
      colorImage: true,
      initials: chip.initials,
      action,
    }],
  };
}

@Component({
  selector: 'transactions-content',
  templateUrl: './transactions-content.component.html',
  styleUrls: ['./transactions-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    ReactiveFormsModule,
    MatTooltipModule,
    MatMenuModule,
    MatTreeModule,
    MatCheckboxModule,
    MatIconModule,
    TidyUiComponentsModule,
    SharedModule,
    MonthAttributionSwitchComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TransactionsContentComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private ufb = inject(UntypedFormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private propertyTransactionsService = inject(PropertyTransactionsService);
  private financialConnectionsService = inject(FinancialConnectionsService);
  private addressesQueries = inject(AddressesQueriesService);
  private financeTypes = inject(FinanceTypesService);
  private bankConnectFlow = inject(BankConnectFlowService);
  private bankRows = inject(BankRowsService);
  private util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly relatedRecords = inject(TransactionRelatedRecordsService);
  private readonly monthAttribution = inject(MonthAttributionService);

  readonly windowService = inject(WindowService);
  private aboutThisPageService = inject(AboutThisPageService);

  private panelClosedSubscription$: Subscription;

  readonly sourceItems: SelectModel[] = [
    { value: 'bank', viewValue: 'From bank' },
    { value: 'manual', viewValue: 'Added manually' },
    { value: 'tidy', viewValue: 'From TIDY' },
  ];
  readonly dateRangeOptions = [
    { viewValue: 'Custom', value: 'custom' },
    { viewValue: 'This Month', value: 'this_month' },
    { viewValue: 'Last 3 Months', value: 'last_3_months' },
    { viewValue: 'This Year', value: 'this_year' },
    { viewValue: 'All Time', value: 'all_time' },
  ];
  dateRangeForm: UntypedFormGroup = this.ufb.group({ start: '', end: '', customStringSelect: '' });
  needsAttentionOnly = false;
  dateFrom: string | null = null;
  dateTo: string | null = null;

  // Same tree-in-a-menu filter as the Bills page: one "Filter" button, chips for what's picked.
  private _transformer = (node: ItemNode, level: number): FlatNode => ({
    expandable: !!node.children && node.children.length > 0,
    name: node.name,
    level,
    data: node,
  });
  treeControl = new FlatTreeControl<FlatNode>(node => node.level, node => node.expandable);
  treeFlattener = new MatTreeFlattener(this._transformer, node => node.level, node => node.expandable, node => node.children);
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  hasChild = (_: number, node: FlatNode) => node.expandable;
  chips: ChipFilter[] = [];
  selectedFilters: Record<FilterGroup, string[]> = { address: [], category: [], source: [] };
  /**
   * The name of the P&L group or total a cell was opened from ("Cleaning & Turnover",
   * "Total net income"). Its categories then show as that one chip, not one chip each.
   */
  private categoryGroupLabel: string | null = null;
  /** Mirrors the date picker's own option so a deep link can land on "Custom". */
  dateRangeOption = 'all_time';
  /**
   * YYYY-MM when a P&L cell sent us here. Under accrual the API lists every row whose period
   * touches the month, a stay running into it included, which a plain date range would miss;
   * under cash, the rows whose money moved in it.
   */
  yearMonth: string | null = null;
  /** The P&L's accrual or cash reading, which decides which transactions a cell's month holds. */
  readonly attribution = this.monthAttribution.value;
  monthLabel = '';

  transactions: FinancialTransaction[] = [];
  views: TransactionView[] = [];
  connectedAccounts: FinancialConnectionsAccount[] = [];
  categoryItems: SelectModel[] = [];
  propertyItems: SelectModel[] = [];
  headers: string[] = [];
  rows: any[] = [];
  isDataLoaded = false;
  loadingMore = false;
  hasMore = false;
  connecting = false;
  exporting = false;
  errorMessage: string;
  emptyStateMessage = EMPTY_NO_TRANSACTIONS;
  showConnectPrompt = false;
  showAboutThisPage = false;
  overlayTop: string;
  private _transactionsHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;
  private page = 1;
  /** Bumped by every fresh load, so a slower earlier response cannot land on top of it. */
  private loadSeq = 0;
  private addresses: Address[] = [];
  private categoryLabels = new Map<string, string>();
  private propertyLabels = new Map<number, string>();
  private propertyColors = new Map<number, string | undefined>();
  /** Bank row id → "Bank ····1234". The P&L row itself does not name its bank. */
  private bankLabelByBankRowId = new Map<number, string>();
  /** Set when exactly one bank is connected: every bank row carries the same label. */
  private singleBankLabel: string | null = null;
  /** Job / reservation ids → summaries, filled after each load; ids stay visible until then. */
  private relatedIndex: RelatedRecords = { jobs: new Map(), reservations: new Map() };
  @ViewChild('transactionsHeader', { read: ElementRef }) set transactionsHeader(el: ElementRef) {
    this._transactionsHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    }
  }
  @ViewChild('tidyTable') tidyTable: any;

  readonly boundLoadMore = () => this.loadMore();
  readonly boundAddAccount = () => this.addAccount();

  async ngOnInit() {
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('transactions');
    if (this.showAboutThisPage) {
      this.loadMockData();
      return;
    }
    // On mobile the secondary pages are full pages; coming back lands here without
    // re-running ngOnInit, so refresh when a child page flagged a change.
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(async () => {
        if (this.windowService.isDesktopRes || !this.isDataLoaded) { return; }
        if (await this.storage.retrieve('didChangeTransactions')) {
          await this.storage.delete('didChangeTransactions');
          this.loadTransactions();
        }
      });
    this.readUrlFilters();
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load transactions';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._transactionsHeader) {return;}
      const top = this._transactionsHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top  }px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  ngOnDestroy() {
    this.overlayResizeObserver?.disconnect();
    this.panelClosedSubscription$?.unsubscribe();
  }

  private loadMockData() {
    this.buildHeaders();
    this.connectedAccounts = [{ id: 1 } as any];
    const properties = ['123 Main St', 'Luxury Villa', 'Mountain Retreat', 'Beach Bungalow', 'Cozy Cottage', 'Desert Oasis'];
    const mock = (id: number, date: string, amount: number, type: 'Debit' | 'Credit', description: string, category: string | null, propertyIndex: number) => ({
      id,
      period_start_date: date,
      period_end_date: date,
      transacted_on: date,
      transacted_at_origin: 'recorded',
      amount,
      transaction_type: type,
      description,
      source: 'bank',
      address_id: propertyIndex + 1,
      monthly_finance_type: category ? { key: category.toLowerCase().replace(/ /g, '_'), label: category } : null,
      suggestion: category ? null : { monthly_finance_type_key: 'consumables', confidence: 'low' },
      status: 'processed',
    });
    const mockTransactions = [
      mock(1, '2025-04-05', 8500, 'Debit', 'Cleaning Service - 123 Main St', 'Cleaning Service', 0),
      mock(2, '2025-04-03', 6000, 'Debit', 'Pool Maintenance - Luxury Villa', 'Pool / Spa / Hot Tub', 1),
      mock(3, '2025-04-01', 125000, 'Credit', 'Booking Payment - Mountain Retreat', 'Short-Term Nightly Revenue', 2),
      mock(4, '2025-03-28', 4500, 'Debit', 'Supplies Restock - Beach Bungalow', null, 3),
      mock(5, '2025-03-25', 95000, 'Credit', 'Booking Payment - Cozy Cottage', 'Short-Term Nightly Revenue', 4),
      mock(6, '2025-03-22', 15000, 'Debit', 'HVAC Repair - Desert Oasis', 'HVAC Service', 5),
      mock(7, '2025-03-20', 8500, 'Debit', 'Cleaning Service - Mountain Retreat', 'Cleaning Service', 2),
      mock(8, '2025-03-18', 175000, 'Credit', 'Booking Payment - 123 Main St', 'Short-Term Nightly Revenue', 0),
      mock(9, '2025-03-15', 3200, 'Debit', 'Pest Control - Beach Bungalow', 'Pest Control', 3),
      mock(10, '2025-03-13', 8500, 'Debit', 'Cleaning Service - Cozy Cottage', 'Cleaning Service', 4),
      mock(11, '2025-03-10', 110000, 'Credit', 'Booking Payment - Desert Oasis', 'Short-Term Nightly Revenue', 5),
      mock(12, '2025-03-08', 25000, 'Debit', 'Landscaping - Luxury Villa', 'Landscaping', 1),
      mock(13, '2025-03-05', 7500, 'Debit', 'Carpet Cleaning - 123 Main St', null, 0),
      mock(14, '2025-03-03', 85000, 'Credit', 'Booking Payment - Luxury Villa', 'Short-Term Nightly Revenue', 1),
      mock(15, '2025-03-01', 5500, 'Debit', 'Linen Service - Mountain Retreat', 'Linens / Laundry Service', 2),
      mock(16, '2025-02-27', 12000, 'Debit', 'Window Cleaning - Beach Bungalow', 'General Repairs', 3),
      mock(17, '2025-02-25', 145000, 'Credit', 'Booking Payment - 123 Main St', 'Short-Term Nightly Revenue', 0),
      mock(18, '2025-02-22', 8500, 'Debit', 'Cleaning Service - Desert Oasis', 'Cleaning Service', 5),
      mock(19, '2025-02-20', 4000, 'Debit', 'Trash Removal - Cozy Cottage', 'Trash / Recycling', 4),
      mock(20, '2025-02-18', 200000, 'Credit', 'Booking Payment - Mountain Retreat', 'Short-Term Nightly Revenue', 2),
      mock(21, '2025-02-15', 35000, 'Debit', 'Plumbing Repair - Luxury Villa', 'Plumbing Repairs', 1),
      mock(22, '2025-02-12', 8500, 'Debit', 'Cleaning Service - Beach Bungalow', 'Cleaning Service', 3),
      mock(23, '2025-02-10', 65000, 'Credit', 'Booking Payment - Cozy Cottage', 'Short-Term Nightly Revenue', 4),
      mock(24, '2025-02-08', 9500, 'Debit', 'Deep Cleaning - Desert Oasis', 'Cleaning Service', 5),
    ] as any[];
    this.propertyLabels = new Map(properties.map((label, index): [number, string] => [index + 1, label]));
    this.propertyItems = properties.map((label, index) => ({ value: String(index + 1), viewValue: label }));
    this.buildTree();
    this.transactions = mockTransactions;
    this.hasMore = false;
    this.rebuild();
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.aboutThisPageService.dismissAboutThisPage('transactions');
    this.transactions = [];
    this.rows = [];
    this.views = [];
    this.connectedAccounts = [];
    this.isDataLoaded = false;
    this.cdr.markForCheck();
    this.loadPage();
  }

  async loadPage() {
    this.buildHeaders();
    const [accounts, categories, addresses] = await Promise.all([
      this.financialConnectionsService.getConnectedAccounts().catch(() => [] as FinancialConnectionsAccount[]),
      this.financeTypes.getFinanceTypes().catch(() => []),
      this.addressesQueries.getAddressesOnce().catch(() => [] as Address[]),
    ]);
    this.connectedAccounts = accounts.filter(account => account.status === 'active');
    this.addresses = addresses;
    this.categoryLabels = new Map(categories.map((type): [string, string] => [type.key, type.label]));
    this.categoryItems = categories.map(type => this.financeTypes.toSelectItem(type));
    this.propertyLabels = new Map(addresses.map((address): [number, string] => [address.id, address['address_name'] || address.address1 || `Property #${address.id}`]));
    this.propertyColors = new Map(addresses.map((address): [number, string | undefined] => [address.id, this.addressIconColor(address.color)]));
    this.propertyItems = addresses.map(address => ({
      value: String(address.id),
      viewValue: this.propertyLabels.get(address.id),
      tagIcon: PROPERTY_ICON,
      tagColor: this.propertyColors.get(address.id),
    }));
    this.buildTree();
    this.buildFilterChips();
    await this.loadTransactions();
    this.loadBankLabels();
  }

  private buildTree(): void {
    const groups: ItemNode[] = [
      { name: 'Property', children: this.propertyItems.map(item => ({ name: item.viewValue, value: item.value, checked: false })) },
      { name: 'Category', children: this.categoryItems.map(item => ({ name: item.viewValue, value: item.value, checked: false })) },
      { name: 'Source', children: this.sourceItems.map(item => ({ name: item.viewValue, value: item.value, checked: false })) },
    ];
    groups.forEach((group, index) => group.children.forEach(child => { child.parentId = index; }));
    this.dataSource.data = groups;
  }

  /** Same rule the weekly schedule uses: the two default colors mean "no custom color". */
  private addressIconColor(color: string | null | undefined): string | undefined {
    return color && !DEFAULT_ADDRESS_COLORS.includes(color) ? color : undefined;
  }

  /** Runs after the first paint: walks each bank's rows so the Source column can name the bank. */
  private async loadBankLabels(): Promise<void> {
    this.singleBankLabel = null;
    this.bankLabelByBankRowId = new Map();
    if (this.connectedAccounts.length === 0) { return; }
    // One bank means every row resolves to the same label, so skip the row walk
    // (it costs up to five requests per connection and runs on every panel close).
    if (this.connectedAccounts.length === 1) {
      this.singleBankLabel = this.bankRows.connectionLabel(this.connectedAccounts[0]);
      this.rebuild();
      return;
    }
    try {
      const rows = await this.bankRows.loadRecentBankRows(this.connectedAccounts);
      this.bankLabelByBankRowId = new Map(rows.map((row): [number, string] => [row.id, row.connectionLabel]));
      this.rebuild();
    } catch {
      // The list still works without bank names; they are a nicety.
    }
  }

  async loadTransactions(): Promise<void> {
    const load = ++this.loadSeq;
    this.page = 1;
    try {
      const rows = await this.propertyTransactionsService.getFinancialTransactions(this.queryParams(1));
      if (load !== this.loadSeq) { return; }
      this.transactions = rows || [];
      this.hasMore = this.transactions.length === PAGE_SIZE;
      this.errorMessage = '';
    } catch (err) {
      if (load !== this.loadSeq) { return; }
      this.transactions = [];
      this.hasMore = false;
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load transactions';
    }
    this.isDataLoaded = true;
    this.rebuild();
    this.resolveRelatedLabels();
  }

  async loadMore(): Promise<void> {
    if (this.loadingMore || !this.hasMore) { return; }
    const load = this.loadSeq;
    this.loadingMore = true;
    this.cdr.markForCheck();
    try {
      const rows = await this.propertyTransactionsService.getFinancialTransactions(this.queryParams(this.page + 1));
      // A new filter or reading reloaded the list meanwhile; this page belongs to the old one.
      if (load !== this.loadSeq) { return; }
      this.page += 1;
      this.transactions = [...this.transactions, ...(rows || [])];
      this.hasMore = (rows || []).length === PAGE_SIZE;
    } catch (err) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to load more transactions');
    } finally {
      this.loadingMore = false;
      this.rebuild();
      this.resolveRelatedLabels();
    }
  }

  private queryParams(page: number): FinancialTransactionParams {
    return { page, per_page: PAGE_SIZE, ...this.filterParams() };
  }

  private filterParams(): FinancialTransactionFilters {
    // Each filter takes its values comma-separated, so every pick is narrowed by the API.
    const list = (group: FilterGroup): string | undefined =>
      this.selectedFilters[group].length ? this.selectedFilters[group].join(',') : undefined;
    return {
      address_id: list('address'),
      monthly_finance_type_key: list('category'),
      source: list('source'),
      needs_attention: this.needsAttentionOnly || undefined,
      // A month counts the periods running into it; a plain range only sees where a row starts.
      year_month: this.yearMonth ?? undefined,
      month_attribution: this.yearMonth ? this.attribution() : undefined,
      date_from: this.yearMonth ? undefined : this.dateFrom ?? undefined,
      date_to: this.yearMonth ? undefined : this.dateTo ?? undefined,
    };
  }

  /**
   * A Monthly P&L cell hands its filters over in the URL, as comma-separated lists. They
   * are read once and then dropped, so leaving this tab and coming back does not silently
   * reapply them. Runs before the first paint so the date picker opens on the right range.
   */
  private readUrlFilters(): void {
    const params = this.route.snapshot.queryParams;
    const list = (raw: unknown): string[] =>
      raw ? String(raw).split(',').map(value => value.trim()).filter(Boolean) : [];
    const categories = list(params['category']);
    const properties = list(params['propertyId']);
    const month: string | null = params['month'] || null;
    const attribution = parseMonthAttribution(params[MONTH_ATTRIBUTION_QUERY_PARAM]);
    if (attribution) { this.attribution.set(attribution); }
    if (!categories.length && !properties.length && !month) { return; }
    this.selectedFilters.category = categories;
    this.selectedFilters.address = properties;
    this.categoryGroupLabel = categories.length > 1 ? (params['categoryLabel'] || null) : null;
    const start = month ? LuxonDateTime.fromFormat(month, 'yyyy-MM') : null;
    if (start?.isValid) {
      // The month drives the query; the range is what the picker shows for it.
      this.yearMonth = month;
      this.dateFrom = start.toFormat('yyyy-MM-dd');
      this.dateTo = start.endOf('month').toFormat('yyyy-MM-dd');
      this.dateRangeOption = 'custom';
      this.dateRangeForm.patchValue({
        customStringSelect: 'custom',
        start: start.startOf('day').toISO(),
        end: start.endOf('month').endOf('day').toISO(),
      });
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        category: null,
        categoryLabel: null,
        propertyId: null,
        month: null,
        [MONTH_ATTRIBUTION_QUERY_PARAM]: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Accrual lists what a month's P&L cell counts, a stay running in from the month before
   * included; cash lists what was paid in it. Flipping here flips the P&L too.
   */
  setAttribution(value: MonthAttribution): void {
    if (this.showAboutThisPage || value === this.attribution()) { return; }
    this.attribution.set(value);
    this.loadTransactions();
  }

  private get isCashMonth(): boolean {
    return !!this.yearMonth && this.attribution() === 'cash';
  }

  /** "August 2026" while a month opened from a P&L cell is listed; the rows' share tooltips name it. */
  private buildMonthLabel(): void {
    const month = this.yearMonth ? LuxonDateTime.fromFormat(this.yearMonth, 'yyyy-MM') : null;
    this.monthLabel = month?.isValid ? month.toFormat('MMMM yyyy') : '';
  }

  private formatWhole(cents: number): string {
    return (Math.abs(cents) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  /** One chip per picked value, named from the labels the page has loaded. */
  private buildFilterChips(): void {
    const chips: ChipFilter[] = [];
    for (const value of this.selectedFilters.address) {
      const addressId = Number(value);
      chips.push({
        displayParentName: FILTER_GROUP_NAMES.address,
        parentName: 'address',
        name: this.propertyLabels.get(addressId) || `Property #${addressId}`,
        value,
        icon: FILTER_ICONS.address,
        color: this.propertyColors.get(addressId),
      });
    }
    const categories = this.selectedFilters.category;
    if (this.categoryGroupLabel || categories.length > MAX_CATEGORY_CHIPS) {
      // A whole group or total is dozens of categories: one chip stands for all of them.
      chips.push({
        displayParentName: FILTER_GROUP_NAMES.category,
        parentName: 'category',
        name: this.categoryGroupLabel || `${categories.length} categories`,
        value: ALL_PICKED_CATEGORIES,
        icon: FILTER_ICONS.category,
      });
    } else {
      for (const value of categories) {
        chips.push({
          displayParentName: FILTER_GROUP_NAMES.category,
          parentName: 'category',
          name: this.categoryLabels.get(value) || value,
          value,
          icon: FILTER_ICONS.category,
        });
      }
    }
    for (const value of this.selectedFilters.source) {
      chips.push({
        displayParentName: FILTER_GROUP_NAMES.source,
        parentName: 'source',
        name: this.sourceItems.find(item => item.value === value)?.viewValue || value,
        value,
        icon: FILTER_ICONS.source,
      });
    }
    this.chips = chips;
    this.cdr.markForCheck();
  }

  checkNode(node: FlatNode, event: MatCheckboxChange): void {
    if (this.showAboutThisPage) { return; }
    const group = this.groupOf(node);
    if (!group) { return; }
    const value = String(node.data.value);
    const picked = this.selectedFilters[group].filter(item => item !== value);
    this.selectedFilters[group] = event.checked ? [...picked, value] : picked;
    // Once categories are picked by hand the set no longer is the group it came from.
    if (group === 'category') { this.categoryGroupLabel = null; }
    this.buildFilterChips();
    this.loadTransactions();
  }

  getNodeChecked(node: FlatNode): boolean {
    const group = this.groupOf(node);
    return !!group && this.selectedFilters[group].includes(String(node.data?.value));
  }

  removeChip(chip: ChipFilter): void {
    if (this.showAboutThisPage) { return; }
    const group = chip.parentName as FilterGroup;
    if (chip.value === ALL_PICKED_CATEGORIES) {
      this.selectedFilters.category = [];
      this.categoryGroupLabel = null;
    } else {
      this.selectedFilters[group] = this.selectedFilters[group].filter(value => value !== String(chip.value));
    }
    this.buildFilterChips();
    this.loadTransactions();
  }

  private groupOf(node: FlatNode): FilterGroup | null {
    const parentId = node.data?.parentId;
    if (parentId === undefined || parentId === null) { return null; }
    return FILTER_GROUPS[this.dataSource.data[parentId]?.name] ?? null;
  }

  toggleNeedsAttention(value: boolean): void {
    this.needsAttentionOnly = value;
    this.loadTransactions();
  }

  selectStringDateRange(selection: string | null): void {
    this.dateRangeOption = selection || 'all_time';
    this.yearMonth = null;
    const today = LuxonDateTime.local();
    let start: LuxonDateTime | null = null;
    switch (selection) {
      case 'this_month': start = today.startOf('month'); break;
      case 'last_3_months': start = today.minus({ months: 3 }); break;
      case 'this_year': start = today.startOf('year'); break;
      default: start = null;
    }
    this.dateFrom = start ? start.toFormat('yyyy-MM-dd') : null;
    this.dateTo = start ? today.toFormat('yyyy-MM-dd') : null;
    this.loadTransactions();
  }

  selectCustomDateRange(range: UntypedFormGroup | { start: Date; end: Date } | null): void {
    const value: any = range && 'value' in range ? range.value : range;
    if (!value?.start || !value?.end) { return; }
    this.dateRangeOption = 'custom';
    this.yearMonth = null;
    this.dateFrom = LuxonDateTime.fromJSDate(new Date(value.start)).toFormat('yyyy-MM-dd');
    this.dateTo = LuxonDateTime.fromJSDate(new Date(value.end)).toFormat('yyyy-MM-dd');
    this.loadTransactions();
  }

  buildHeaders(): void {
    this.headers = [
      'Date',
      'Description',
      'Amount',
      'Category',
      'Property',
      'Job',
      'Reservation',
      'last-column',
    ];
  }

  private rebuild(): void {
    // First: the rows' share tooltips name the month.
    this.buildMonthLabel();
    this.views = this.transactions.map(txn => this.toView(txn));
    this.rows = this.views.map(view => this.toTableRow(view));
    // The list is filtered server side, so an empty page means "no matches"
    // whenever a filter is on — not "connect a bank account".
    const filtered = this.hasActiveFilters;
    this.emptyStateMessage = filtered ? EMPTY_NO_MATCHES : EMPTY_NO_TRANSACTIONS;
    this.showConnectPrompt = !filtered && this.connectedAccounts.length === 0;
    this.cdr.markForCheck();
  }

  private get hasActiveFilters(): boolean {
    return !!(this.selectedFilters.address.length || this.selectedFilters.category.length
      || this.selectedFilters.source.length || this.needsAttentionOnly || this.dateFrom);
  }

  private toView(txn: FinancialTransaction): TransactionView {
    const categoryLabel = txn.monthly_finance_type?.label || (txn.monthly_finance_type?.key ? this.categoryLabels.get(txn.monthly_finance_type.key) : '') || '';
    const propertyLabel = txn.address_id ? this.propertyLabels.get(txn.address_id) || `Property #${txn.address_id}` : '';
    const suggestionKey = txn.suggestion?.monthly_finance_type_key;
    const suggestionLabel = !categoryLabel && suggestionKey ? (this.categoryLabels.get(suggestionKey) || suggestionKey) : '';
    const bankLabel = txn.financial_connection_transaction_id
      ? (this.singleBankLabel ?? this.bankLabelByBankRowId.get(txn.financial_connection_transaction_id))
      : undefined;
    const isTidy = txn.source === 'tidy';
    const permissions = permissionsOf(txn);
    const jobId = this.relatedRecords.jobIdOf(txn);
    const reservationId = this.relatedRecords.reservationIdOf(txn);
    const period = periodLabel(txn);
    const hasRange = !!txn.period_end_date && txn.period_end_date !== txn.period_start_date;
    // Under cash the paid day is what put the row in the month, so it is the date shown.
    const cashMonth = this.isCashMonth && !!txn.transacted_on;
    const dateLabel = cashMonth ? paidOnLabel(txn, true) : period;
    const dayShown = cashMonth ? txn.transacted_on : txn.period_start_date;
    const share = txn.month_share_amount;
    const isPartShare = typeof share === 'number' && share !== txn.amount;
    const amountShown = isPartShare ? share : txn.amount;
    return {
      txn,
      formattedAmount: this.propertyTransactionsService.formatAmount({ amount: amountShown, transaction_type: txn.transaction_type }),
      wholeAmountNote: isPartShare ? `of ${this.formatWhole(txn.amount)}` : '',
      amountShown,
      categoryLabel,
      propertyLabel,
      propertyColor: txn.address_id ? this.propertyColors.get(txn.address_id) : undefined,
      sourceLabel: isTidy ? 'TIDY' : txn.source === 'manual' ? 'Added manually' : (bankLabel || 'Bank'),
      needsAttention: (permissions.monthly_finance_type && !categoryLabel) || (permissions.address && !txn.address_id),
      suggestionLabel,
      isTidy,
      dateLabel,
      dayShown: dayShown || '',
      isUpcoming: !!dayShown && dayShown > LuxonDateTime.local().toFormat('yyyy-MM-dd'),
      jobId,
      reservationId,
      job: jobId ? this.jobChip(txn, jobId, period) : null,
      reservation: reservationId ? this.reservationChip(txn, reservationId, hasRange ? period : '') : null,
      canEditCategory: permissions.monthly_finance_type,
      canEditProperty: permissions.address,
    };
  }

  /** Until the summaries resolve, a TIDY row's own date names its job; a customer row shows the id. */
  private jobChip(txn: FinancialTransaction, jobId: number, dateLabel: string): RecordChip {
    const summary = this.relatedIndex.jobs.get(jobId);
    if (summary?.dateLabel) { return { label: summary.dateLabel, icon: summary.iconUrl, initials: '' }; }
    return {
      label: txn.source_record_type === 'Job' && dateLabel ? dateLabel : `Job #${jobId}`,
      icon: summary?.iconUrl ?? JOB_ICON_FALLBACK,
      initials: '',
    };
  }

  private reservationChip(txn: FinancialTransaction, reservationId: number, rangeLabel: string): RecordChip {
    const summary = this.relatedIndex.reservations.get(reservationId);
    if (summary) { return { label: summary.dateLabel, icon: RESERVATION_COLOR_ICON, initials: summary.guestName }; }
    return {
      label: txn.source_record_type === 'GuestReservation' && rangeLabel ? rangeLabel : `Reservation #${reservationId}`,
      icon: RESERVATION_COLOR_ICON,
      initials: '',
    };
  }

  private toTableRow(view: TransactionView): any {
    const txn = view.txn;
    const suggestionKey = txn.suggestion?.monthly_finance_type_key;
    const categoryItems = view.categoryLabel
      ? this.categoryItems
      : [
        CATEGORY_PLACEHOLDER,
        ...(view.suggestionLabel && suggestionKey ? [{ value: suggestionKey, viewValue: `Suggested: ${view.suggestionLabel}` }] : []),
        ...this.categoryItems,
      ];
    return {
      id: txn.id,
      // A future-dated row shows a clock after the date; the tooltip says it has not happened yet.
      'Date': {
        value: view.dateLabel,
        action: null,
        ...(view.isUpcoming ? { icon: UPCOMING_ICON, iconTooltip: UPCOMING_TOOLTIP, showIconOnRight: true, iconClass: 'txn-upcoming-icon' } : {}),
      },
      'Description': { value: txn.description || '', action: null },
      // A month's share reads "+$6,268.80 of $13,164.90", with the why in the tooltip.
      'Amount': {
        value: view.wholeAmountNote
          ? `${view.formattedAmount} <span class="txn-share-of">${view.wholeAmountNote}</span>`
          : view.formattedAmount,
        action: null,
        ...(view.wholeAmountNote ? {
          icon: SHARE_ICON,
          iconTooltip: `${this.monthLabel} counts ${view.formattedAmount.replace(/^[+-]/, '')} ${view.wholeAmountNote}: the part for its days in ${this.monthLabel}.`,
          showIconOnRight: true,
          iconClass: 'txn-upcoming-icon',
        } : {}),
      },
      // The dropdowns stay on every row and grey out where the row's permissions say no (a TIDY row's
      // category and property belong to its job or reservation).
      'Category': {
        value: txn.monthly_finance_type?.key ?? '',
        selectItems: categoryItems,
        action: null,
        disabled: !view.canEditCategory,
        ...(view.categoryLabel || !view.canEditCategory ? {} : { icon: ATTENTION_ICON, iconTooltip: 'Needs a category to count in your P&L' }),
      },
      'Property': {
        value: txn.address_id ? String(txn.address_id) : '',
        // The collapsed value draws itself, so it can carry the property's colour like the options do.
        valueLabel: view.propertyLabel || PROPERTY_PLACEHOLDER.viewValue,
        tagIcon: PROPERTY_ICON,
        tagColor: view.propertyColor,
        selectItems: txn.address_id ? this.propertyItems : [PROPERTY_PLACEHOLDER, ...this.propertyItems],
        action: null,
        disabled: !view.canEditProperty,
        ...(txn.address_id || !view.canEditProperty ? {} : { icon: ATTENTION_ICON, iconTooltip: 'Needs a property to count in your P&L' }),
      },
      // One small button per record, the icon or the guest's initials inside it; the button opens the record.
      'Job': recordButtonCell(view.job, () => this.openJob(view)),
      'Reservation': recordButtonCell(view.reservation, () => this.openReservation(view)),
      'last-column': { value: '', action: null },
    };
  }

  onTableSelectChange(event: { value: string; column: string; element: any }): void {
    const view = this.views.find(v => v.txn.id === event.element.id);
    if (!view || !event.value) { return; }
    if (event.column === 'Category' && view.canEditCategory) {
      this.updateTransaction(view, { monthly_finance_type_key: event.value });
    } else if (event.column === 'Property' && view.canEditProperty) {
      this.updateTransaction(view, { address_id: Number(event.value) });
    } else {
      // The row may not change this field: put the select back instead of showing a value that never saves.
      this.restoreRow(view);
    }
  }

  onTableColumnClick(event: { column: string; element: any }): void {
    const view = this.views.find(v => v.txn.id === event.element.id);
    if (!view) {return;}
    this.openTransaction(view);
  }

  /**
   * Saves an inline edit and patches the row where it sits. Rebuilding the list
   * would re-render the table and drop rows the needs-attention filter no longer
   * matches, which reads as the page jumping; the order stays until the next load.
   */
  private async updateTransaction(view: TransactionView, changes: { monthly_finance_type_key?: string; address_id?: number }): Promise<void> {
    if (this.showAboutThisPage) { return; }
    if (this.demoBlock.block()) {
      this.restoreRow(view);
      return;
    }
    try {
      const [updated] = await this.propertyTransactionsService.updateFinancialTransactions([{ id: view.txn.id, ...changes }]);
      const merged: FinancialTransaction = updated ?? {
        ...view.txn,
        ...(changes.address_id ? { address_id: changes.address_id } : {}),
        ...(changes.monthly_finance_type_key
          ? { monthly_finance_type: { key: changes.monthly_finance_type_key, label: this.categoryLabels.get(changes.monthly_finance_type_key) || changes.monthly_finance_type_key } }
          : {}),
      };
      // The update answers without the opened month's share. The row keeps the one it was listed
      // with while it still belongs to the opened cell; moved to another category or property, it does not.
      const share = this.stillMatchesFilters(merged) ? view.txn.month_share_amount : undefined;
      this.patchRowInPlace(view, { ...merged, month_share_amount: merged.month_share_amount ?? share });
      this.util.showSuccess('Saved');
    } catch (err) {
      this.restoreRow(view);
      const isUnassignedRow = !view.txn.address_id && err?.status === 403;
      this.util.showError(isUnassignedRow
        ? 'This transaction has no property yet, so it cannot be categorized on its own. Pick the property first.'
        : err?.error?.message || err?.message || 'Unable to update the transaction.');
    }
  }

  private stillMatchesFilters(txn: FinancialTransaction): boolean {
    const { category, address } = this.selectedFilters;
    const categoryKey = txn.monthly_finance_type?.key;
    const categoryMatches = !category.length || (!!categoryKey && category.includes(categoryKey));
    const propertyMatches = !address.length || (!!txn.address_id && address.includes(String(txn.address_id)));
    return categoryMatches && propertyMatches;
  }

  private patchRowInPlace(view: TransactionView, merged: FinancialTransaction): void {
    const index = this.transactions.findIndex(txn => txn.id === merged.id);
    if (index >= 0) { this.transactions[index] = merged; }
    const fresh = this.toView(merged);
    Object.assign(view, fresh);
    const row = this.rows.find(r => r.id === merged.id);
    if (row) { Object.assign(row, this.toTableRow(fresh)); }
    this.refreshRows();
  }

  /** Puts the inline select back to the saved value after a failed or blocked save. */
  private restoreRow(view: TransactionView): void {
    const row = this.rows.find(r => r.id === view.txn.id);
    if (row) { Object.assign(row, this.toTableRow(view)); }
    this.refreshRows();
  }

  /**
   * tidy-table is OnPush and the rows above are patched in place, so a new array
   * reference is what makes it re-read them. The row objects keep their identity,
   * so the table re-renders the changed cells without rebuilding the list.
   */
  private refreshRows(): void {
    this.rows = [...this.rows];
    this.cdr.markForCheck();
  }

  openTransaction(view: TransactionView): void {
    if (this.showAboutThisPage) {return;}
    this.goToPage('transaction-detail', {
      transaction: view.txn,
      addToPnl: null,
      connectionLabel: view.txn.source === 'bank' && view.sourceLabel !== 'Bank' ? view.sourceLabel : null,
      propertyLabel: view.propertyLabel || null,
      monthLabel: this.monthLabel || null,
    }, TransactionDetailPage);
  }

  async openJob(view: TransactionView, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (this.showAboutThisPage || !view.jobId) { return; }
    try {
      await this.relatedRecords.openJob(view.jobId, view.txn.address_id);
    } catch (err) {
      this.util.showError(err?.error?.message || err?.message || 'Unable to open the job.');
    }
  }

  async openReservation(view: TransactionView, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (this.showAboutThisPage || !view.reservationId) { return; }
    try {
      await this.relatedRecords.openReservation(view.reservationId, view.txn.address_id, view.propertyLabel);
    } catch (err) {
      this.util.showError(err?.error?.message || err?.message || 'Unable to open the reservation.');
    }
  }

  /** Runs after the rows paint: names the jobs and reservations the page points at, then repaints the chips. */
  private async resolveRelatedLabels(): Promise<void> {
    if (this.showAboutThisPage || this.transactions.length === 0) { return; }
    try {
      const records = await this.relatedRecords.recordsFor(this.transactions);
      this.relatedIndex = {
        jobs: new Map([...this.relatedIndex.jobs, ...records.jobs]),
        reservations: new Map([...this.relatedIndex.reservations, ...records.reservations]),
      };
      this.rebuild();
    } catch {
      // The chips still work with ids; names are a nicety.
    }
  }

  openManageConnections(): void {
    if (this.showAboutThisPage) {return;}
    if (this.demoBlock.block()) { return; }
    this.goToPage('manage-connections', {}, ManageConnectionsPage);
  }

  openAddTransaction(): void {
    if (this.showAboutThisPage) {return;}
    this.goToPage('add-transaction', {}, AddTransactionPage);
  }

  openRules(): void {
    if (this.showAboutThisPage) {return;}
    this.goToPage('transaction-rules', {}, TransactionRulesPage);
  }

  /** Downloads every row the filters match, not only the pages loaded on screen. */
  async exportCsv(): Promise<void> {
    if (this.showAboutThisPage || this.exporting) { return; }
    this.exporting = true;
    const loading = await this.util.showLoading({ message: 'Preparing export...' });
    try {
      const transactions = await this.propertyTransactionsService.getFinancialTransactionsForExport(this.filterParams());
      if (transactions.length === 0) {
        this.util.showError('No transactions to export', 5000);
        return;
      }
      csvDownload({
        data: transactions.map(txn => this.toCsvRow(txn)),
        filename: `TIDY-transactions-${LuxonDateTime.local().toFormat('yyyy-MM-dd')}`,
        delimiter: ',',
        headers: CSV_HEADERS,
      });
    } catch (err) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to export transactions', 10000);
    } finally {
      loading?.dismiss();
      this.exporting = false;
    }
  }

  /**
   * Plain dates and a signed number, so the file sorts and sums in a spreadsheet. Date and
   * Amount come from the row's view, so the file matches the list: under cash in an opened
   * month the paid day, and the month's share when the row spans months.
   */
  private toCsvRow(txn: FinancialTransaction): Record<string, string> {
    const view = this.toView(txn);
    const sign = txn.transaction_type === 'Credit' ? 1 : -1;
    const hasRange = !!txn.period_end_date && txn.period_end_date !== txn.period_start_date;
    const isStay = txn.source_record_type === 'GuestReservation';
    return {
      'Date': view.dayShown,
      // A stay's row ends on its last night; the guest leaves the day after, as the table shows it.
      'Check-out': !hasRange ? '' : isStay ? LuxonDateTime.fromISO(txn.period_end_date).plus({ days: 1 }).toFormat('yyyy-MM-dd') : txn.period_end_date,
      'Description': txn.description || '',
      'Amount': (sign * view.amountShown / 100).toFixed(2),
      'Category': view.categoryLabel,
      'Property': view.propertyLabel,
      'Source': view.sourceLabel,
    };
  }

  /** Only offered here while no bank is connected; afterwards banks are added from Connected Bank Accounts. */
  async addAccount(): Promise<void> {
    if (this.showAboutThisPage) {return;}
    if (this.demoBlock.block()) { return; }
    if (this.connecting) { return; }
    this.connecting = true;
    this.cdr.markForCheck();
    try {
      const result = await this.bankConnectFlow.connectBank();
      if (!result.linked) { return; }
      await this.loadPage();
      if (result.linkedConnections.length > 0) {
        this.goToPage('connection-history', { connections: result.linkedConnections, connection: null, isNewConnection: true }, ConnectionHistoryPage);
      } else {
        // The webhook skips accounts TIDY already has on file, including removed ones.
        this.util.showError('Stripe linked the account, but TIDY already has it on file. If it was removed earlier, re-adding it is not supported yet.', 8000);
      }
    } catch (err) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to connect bank account.');
    } finally {
      this.connecting = false;
      this.cdr.markForCheck();
    }
  }

  private async goToPage(url: string, params: any, component: any): Promise<void> {
    if (this.windowService.isDesktopRes) {
      // Read the panel state in memory instead of awaiting storage, so the dialog opens on the click.
      const isRightSidePanelAlreadyOpen = this.rightSidePanelService.isOpen$.getValue();
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription$?.unsubscribe();
      this.panelClosedSubscription$ = this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(async () => {
        await this.storage.delete('didChangeTransactions');
        await this.loadPage();
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
